/**
 * Stepper component for the asset metadata upload process
 */
import type { Step } from '@/asset/components/asset-metadata-upload/types';
import { Check } from '@/constants/hardfin-standard-icons';
import { Box, Stack, Typography } from '@mui/material';

interface StepperProps {
  currentStep: Step;
}

/**
 * Renders a stepper component to visualize the current step in the upload process
 */
export function Stepper({ currentStep }: StepperProps) {
  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'divider',
            zIndex: 0,
          },
        }}
      >
        {['select-product', 'download-template', 'upload-csv', 'results'].map(
          (step, index) => {
            const isActive = currentStep === step;
            const isCompleted =
              (step === 'select-product' && currentStep !== 'select-product') ||
              (step === 'download-template' &&
                ['upload-csv', 'results'].includes(currentStep)) ||
              (step === 'upload-csv' && currentStep === 'results');

            return (
              <Box
                key={step}
                sx={{
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  backgroundColor: 'white',
                  px: 3,
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    backgroundColor: isCompleted
                      ? 'success.main'
                      : isActive
                        ? 'primary.main'
                        : 'background.paper',
                    border: (theme) =>
                      `2px solid ${
                        isCompleted
                          ? theme.palette.success.main
                          : isActive
                            ? theme.palette.primary.main
                            : theme.palette.divider
                      }`,
                    color: isCompleted || isActive ? 'white' : 'text.secondary',
                    mb: 1,
                  }}
                >
                  {isCompleted ? <Check /> : index + 1}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  {step === 'select-product' && 'Select product'}
                  {step === 'download-template' && 'Download template'}
                  {step === 'upload-csv' && 'Upload CSV'}
                  {step === 'results' && 'Results'}
                </Typography>
              </Box>
            );
          }
        )}
      </Stack>
    </Box>
  );
}
